<template>
  <div class="page-modal-backdrop" @click.self="close">
    <div
      id="page-modal"
      class="page-modal"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      :page-unique-title="pageUniqueTitle"
      :data-page-id="pageUniqueTitle"
      @keydown="onKeydown"
    >
      <header class="page-modal-header navbar navbar-expand-md navbar-dark bg-primary">
        <div class="container-fluid">
          <div class="page-modal-brand">
            <h2 :id="titleId" class="page-modal-title">
              <a class="navbar-brand">{{ title || 'Page' }}</a>
            </h2>
          </div>
          <button ref="closeButton" type="button" class="page-modal-close ms-auto" aria-label="Close" @click="close">&times;</button>
        </div>
      </header>
      <div class="page-modal-body">
        <p v-if="loading" class="page-modal-status" role="status">Loading...</p>
        <p v-else-if="error" class="page-modal-status" role="alert">{{ error }}</p>
        <div v-else-if="loginRequired" class="page-modal-status">
          <h3>Login required</h3>
          <p>You must be logged in to view this page.</p>
        </div>
        <div v-else-if="permissionDenied" class="page-modal-status">
          <h3>No access</h3>
          <p>Your account cannot view this page.</p>
        </div>
        <div v-else>
          <PageOpenBanners :key="pageUniqueTitle" :banners="pageOpenBanners" />
          <PageComponents :components="components" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import PageComponents from './PageComponents.vue'
import PageOpenBanners from './PageOpenBanners.vue'
import { instantiatePageComponent } from '../lib/page-components'
import { shouldRenderView } from '../lib/view-visibility'
import { scopeCss } from '../../../shared/view-css'
import { pageRequestUrl, openPageRedirect } from '../lib/page-open'
import { readLoggedInUser } from '../lib/live-user-storage'

export default {
  name: 'PageModal',
  components: { PageComponents, PageOpenBanners },
  props: {
    target: {
      type: Object,
      required: true
    },
    account: {
      type: String,
      default: 'default'
    },
    application: {
      type: String,
      default: ''
    }
  },
  emits: ['close'],
  provide () {

    return { modalRoute: this.modalRoute }
  },
  data () {

    return {
      titleId: 'page-modal-title',
      title: '',
      components: [],
      loading: true,
      error: '',
      loginRequired: false,
      permissionDenied: false,
      pageOpenBanners: [],
      loadSeq: 0,
      modalRoute: {
        active: true,
        pageUniqueTitle: '',
        accessKey: null,
        segments: [],
        path: ''
      }
    }
  },
  computed: {
    pageUniqueTitle () {

      return (this.target && this.target.pageUniqueTitle) || ''
    }
  },
  watch: {
    target: {
      deep: true,
      immediate: true,
      handler (target) {

        this.applyTarget(target)
        this.load()
        this.$nextTick(() => {

          const button = this.$refs.closeButton

          if (button && typeof button.focus === 'function') button.focus()
        })
      }
    }
  },
  mounted () {

    this._onKeydown = (event) => {

      if (event.key === 'Escape') {

        event.preventDefault()
        this.close()
      }
    }

    document.addEventListener('keydown', this._onKeydown)
    this.$nextTick(() => {

      if (this.$refs.closeButton) this.$refs.closeButton.focus()
    })
  },
  beforeUnmount () {

    document.removeEventListener('keydown', this._onKeydown)
    this.clearCss()
    this.modalRoute.active = false
  },
  methods: {
    close () {

      this.$emit('close')
    },
    applyTarget (target) {

      const next = target || {}

      this.modalRoute.active = true
      this.modalRoute.pageUniqueTitle = next.pageUniqueTitle || ''
      this.modalRoute.accessKey = next.accessKey || null
      this.modalRoute.segments = Array.isArray(next.segments) ? next.segments.slice() : []
      this.modalRoute.path = next.path || ''
    },
    async load () {

      const target = this.target

      if (!target || !target.pageUniqueTitle || !this.application) return

      const loadId = ++this.loadSeq

      this.loading = true
      this.error = ''
      this.loginRequired = false
      this.permissionDenied = false
      this.pageOpenBanners = []

      try {

        const response = await axios.get(pageRequestUrl(
          `${VUE_APP_PROXY_SERVER_URL}/${this.account}/${this.application}/pages/${encodeURIComponent(target.pageUniqueTitle)}`,
          target.accessKey
        ))

        if (loadId !== this.loadSeq) return

        const page = response.data || {}

        this.title = page.title || target.pageUniqueTitle
        this.loginRequired = !!(page.requiresLogin && !readLoggedInUser({
          account: this.account,
          application: this.application
        }))

        if (this.loginRequired) {

          this.components = []
          this.clearCss()
          return
        }

        if (openPageRedirect(this.$router, this.application, page)) {

          this.close()
          return
        }

        this.pageOpenBanners = (page.pageOpen && page.pageOpen.banners) || []
        this.injectCss(page)
        const components = []

        for (const component of page.components || []) {

          if (!shouldRenderView(component)) continue

          const instantiated = instantiatePageComponent(component, {
            pageUniqueTitle: page.uniqueTitle || target.pageUniqueTitle,
            accessKey: target.accessKey
          })

          if (instantiated) components.push(instantiated)
        }

        if (loadId !== this.loadSeq) return

        this.components = components
      } catch (error) {

        if (loadId !== this.loadSeq) return

        const data = error.response && error.response.data
        const denied = error.response && error.response.status === 403
          && data && data.permissionDenied
        const loginRequired = error.response && error.response.status === 401
          && data && data.loginRequired

        this.components = []
        this.clearCss()
        this.permissionDenied = !!denied
        this.loginRequired = !!loginRequired

        if (!denied && !loginRequired) this.error = 'This page could not be opened.'
      } finally {

        if (loadId === this.loadSeq) this.loading = false
      }
    },
    putStyle (id, cssText) {

      if (!cssText || !cssText.trim()) return

      let styleEl = document.getElementById(id)

      if (!styleEl) {

        styleEl = document.createElement('style')
        styleEl.id = id
        document.head.appendChild(styleEl)
      }

      styleEl.textContent = cssText
    },
    injectCss (page) {

      this.clearCss()

      if (page.customCss && page.uniqueTitle) {

        this.putStyle(
          `modal-page-css-${page.uniqueTitle}`,
          scopeCss(`#page-modal[data-page-id="${page.uniqueTitle}"]`, page.customCss)
        )
      }

      const walk = (components) => {

        for (const component of components || []) {

          if (component.customCss && component.uniqueTitle) {

            this.putStyle(
              `modal-view-css-${component.uniqueTitle}`,
              scopeCss(`#page-modal [data-view-id="${component.uniqueTitle}"]`, component.customCss)
            )
          }

          if (component.components) walk(component.components)
        }
      }

      walk(page.components)
    },
    clearCss () {

      document.querySelectorAll('style[id^="modal-page-css-"], style[id^="modal-view-css-"]').forEach((el) => el.remove())
    },
    onKeydown (event) {

      if (event.key !== 'Tab') return

      const focusable = this.$el.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')

      if (!focusable.length) {

        event.preventDefault()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      if (event.shiftKey && document.activeElement === first) {

        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {

        event.preventDefault()
        first.focus()
      }
    }
  }
}
</script>

<style>
.page-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1100;
  background: rgba(20, 20, 20, 0.55);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 4vh 1rem 1rem;
  overflow: auto;
}

.page-modal {
  width: min(960px, 100%);
  max-height: calc(100vh - 5vh);
  display: flex;
  flex-direction: column;
  background: #fff;
  color: #212529;
  text-align: initial;
  border-radius: 8px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.28);
  overflow: hidden;
}

.page-modal-brand {
  display: contents;
}

.page-modal-header .page-modal-title {
  margin: 0;
  min-width: 0;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
}

.page-modal-header .navbar-brand {
  white-space: normal;
}

.page-modal-close {
  border: 0;
  background: transparent;
  color: var(--bs-navbar-brand-color, currentColor);
  font-size: 1.6rem;
  line-height: 1;
  padding: 0 0.25rem;
  cursor: pointer;
}

.page-modal-body {
  overflow: auto;
  min-width: 0;
  min-height: 0;
  padding: 12px 8px 20px;
}

/* A Bootstrap row's horizontal margin is half the gutter, usually -12px.
   This body only pads 8px, so the row sticks out and overflow:auto draws a
   horizontal scrollbar. App CSS that zeroes the margin loses when the theme
   stylesheet is appended again on navigation, so the reset has to live here. */
.page-modal-body .row {
  margin-left: 0;
  margin-right: 0;
}

.page-modal-status {
  text-align: center;
  padding: 2rem 1rem;
}

.page-modal-status h3 {
  color: inherit;
}
</style>
